/**
 * Owen Underwriter v2 — verdict scoring.
 *
 * Combines deterministic eligibility findings with the borrower's existing
 * Mortgage Readiness breakdown into a single 0-100 underwriting score. This
 * replaces the hardcoded 87/100 the legacy mock used.
 *
 * Composition:
 *   - 40% weighted eligibility (sum of finding impacts, capped)
 *   - 60% existing readiness breakdown (uses calculateReadinessScore output)
 *   - Penalty: any 'fail' status caps the verdict at 60 (lender-conditional).
 */

import { calculateReadinessBreakdown } from '../readinessScoring';

const TIERS = [
  { min: 90, status: 'Cleared',                  color: 'success' },
  { min: 75, status: 'Cleared with Conditions',  color: 'success' },
  { min: 60, status: 'Pre-Approved Conditional', color: 'warning' },
  { min: 40, status: 'Needs Significant Work',   color: 'warning' },
  { min: 0,  status: 'Not Eligible',             color: 'danger' },
];

export function tierFor(score) {
  return TIERS.find(t => score >= t.min) || TIERS[TIERS.length - 1];
}

const sumImpact = (findings, predicate) =>
  findings.filter(predicate).reduce((s, f) => s + (f.impact || 0), 0);

export function calculateUnderwritingScore(state, eligibility) {
  const findings = eligibility?.findings || [];
  const hasFail = findings.some(f => f.status === 'fail');

  // Eligibility component (0-40)
  const passImpact = sumImpact(findings, (f) => f.status === 'pass');
  const conditionImpact = sumImpact(findings, (f) => f.status === 'condition') * 0.6;
  const maxPossibleImpact = findings.reduce((s, f) => {
    const ceiling = ['credit_min', 'dti'].includes(f.id) ? 12 : ['ltv', 'documentation'].includes(f.id) ? 10 : 8;
    return s + ceiling;
  }, 0) || 1;
  const eligibilityPct = Math.min(1, (passImpact + conditionImpact) / maxPossibleImpact);
  const eligibilityScore = Math.round(eligibilityPct * 40);

  // Readiness component (0-60)
  const readiness = calculateReadinessBreakdown(state);
  const readinessPct = (readiness?.totalScore || 0) / 100;
  const readinessScore = Math.round(readinessPct * 60);

  let total = eligibilityScore + readinessScore;
  if (hasFail) total = Math.min(total, 60);
  total = Math.max(0, Math.min(100, total));

  return {
    score: total,
    breakdown: {
      eligibilityScore,
      readinessScore,
      eligibilityPct: Math.round(eligibilityPct * 100),
      readinessPct: Math.round(readinessPct * 100),
    },
    tier: tierFor(total),
    hasFail,
  };
}

export default { calculateUnderwritingScore, tierFor };
